/**
 * [MCV-1665] Single source of truth for "does this value contain real HTML
 * emitted by RichTextArea's editor" -- shared by RichTextArea (hydration
 * sanitization), RichTextContent (render branch), stripHtml (search
 * projection) and mirrored in the API's html_to_text.py.
 *
 * The tag-name list is exactly what the editor's ALLOWED_TAGS can produce.
 * The detector requires STRUCTURALLY COMPLETE markup -- a matched
 * opening/closing pair of one allowed tag, or a `<br>`/`<br/>` -- not a lone
 * opening or closing token. The editor never emits an
 * unclosed tag, so a bare opening tag (`<pre>`, `<Epic>`, `<div>`) can only
 * be legacy plain text with a literal angle bracket that happens to name a
 * supported tag (e.g. "Use <pre> for examples"), which must never be parsed
 * as markup or have its token silently stripped.
 */
export const EDITOR_TAG_NAMES = [
  'p',
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'a',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'pre',
] as const;

// A matched opening/closing pair of the SAME allowed tag (`<b>x</b>`,
// `<p>…</p>`), or a standalone `<br>`/`<br/>` -- the one construct the
// editor emits unpaired (Shift+Enter). A lone opening OR closing token
// ("Use <pre> for examples", "Use </pre> to close") is legacy text. A
// literal "<br>" in legacy prose remains the accepted residual ambiguity.
const EDITOR_TAG_PAIR_OR_BR = new RegExp(
  `<(${EDITOR_TAG_NAMES.join('|')})(\\s[^>]*)?>[\\s\\S]*?</\\1>|<br\\s*/?>`,
  'i'
);

/**
 * True only for values containing structurally complete editor markup (a
 * matched pair of one `EDITOR_TAG_NAMES` tag, or a `<br>`/`<br/>`). Legacy plain
 * text with literal angle brackets -- including text that happens to spell
 * out a supported tag name, like "Use <pre> for examples" -- returns false.
 */
export const isEditorHtml = (value: string): boolean => EDITOR_TAG_PAIR_OR_BR.test(value);
